// src/components/events/EventFilters.jsx
import React, { useState, useEffect } from 'react';
import { edgeNodesService } from '../../services/edge-nodes';
import { edgeNodeDisplayName } from '../../utils/edgeNodeDisplayName';

const EventFilters = ({ filters, onFilterChange, onEdgeNodesLoaded }) => {
  const [edgeNodes, setEdgeNodes] = useState([]);
  const [edgeNodesLoading, setEdgeNodesLoading] = useState(true);
  const [edgeNodesError, setEdgeNodesError] = useState(null);

  const fetchEdgeNodes = async () => {
    try {
      console.log('🔍 EventFilters: Fetching edge nodes...');
      setEdgeNodesLoading(true);
      setEdgeNodesError(null);
      
      const nodes = await edgeNodesService.getEdgeNodes();
      console.log('📦 EventFilters: Raw edge nodes data:', nodes);
      console.log('📊 EventFilters: Edge nodes count:', nodes?.length);
      
      if (Array.isArray(nodes) && nodes.length > 0) {
        console.log('🔎 EventFilters: First edge node structure:', nodes[0]);
        setEdgeNodes(nodes);
        // Hand the real list (never the hardcoded fallback) to the page, so the event rows can name sites.
        if (onEdgeNodesLoaded) onEdgeNodesLoaded(nodes);
      } else {
        console.warn('⚠️ EventFilters: No edge nodes returned, using hardcoded fallback');
        setEdgeNodes([
          { celerway_edge_node_id: 'CELERWAY_AGENT_001' },
          { celerway_edge_node_id: 'CELERWAY_AGENT_002' }
        ]);
      }
    } catch (error) {
      console.error('❌ EventFilters: Error fetching edge nodes:', error);
      setEdgeNodesError(error.message || 'Failed to load edge agents');
      console.log('🔄 EventFilters: Using hardcoded fallback agents');
      setEdgeNodes([
        { celerway_edge_node_id: 'CELERWAY_AGENT_001' },
        { celerway_edge_node_id: 'CELERWAY_AGENT_002' }
      ]);
    } finally {
      setEdgeNodesLoading(false);
      console.log('✅ EventFilters: Edge nodes loading complete. Final nodes:', edgeNodes.length);
    }
  };

  useEffect(() => {
    fetchEdgeNodes();
  }, []);

  // Debug: Log edge nodes whenever they change
  useEffect(() => {
    console.log('🔄 EventFilters: edgeNodes state updated:', edgeNodes);
  }, [edgeNodes]);

  const handleChange = (e) => {
    console.log('🎯 EventFilters: handleChange called!');
    const { name, value } = e.target;
    console.log('🔧 EventFilters: Field name:', name);
    console.log('🔧 EventFilters: Field value:', value);
    console.log('📥 EventFilters: Current filters before change:', filters);
    
    let newFilters = { ...filters, [name]: value };
    
    // If changing edge agent filter, also set the alternative field name
    // to ensure backend compatibility
    if (name === 'celerway_edge_node_id') {
      newFilters.edge_node_id = value;
      console.log('✅ EventFilters: Also setting edge_node_id =', value);
    } else if (name === 'edge_node_id') {
      newFilters.celerway_edge_node_id = value;
      console.log('✅ EventFilters: Also setting celerway_edge_node_id =', value);
    }
    
    console.log('📤 EventFilters: New filters object:', newFilters);
    console.log('📤 EventFilters: Calling onFilterChange with:', JSON.stringify(newFilters));
    onFilterChange(newFilters);
    console.log('✅ EventFilters: onFilterChange called successfully');
  };

  const handleTimeRangeChange = (hours) => {
    if (hours === null) {
      // Clear time range
      onFilterChange({ ...filters, start_time: '', end_time: '' });
      return;
    }

    const endTime = new Date();
    const startTime = new Date(endTime.getTime() - (hours * 60 * 60 * 1000));
    
    onFilterChange({
      ...filters,
      start_time: startTime.toISOString().slice(0, 16),
      end_time: endTime.toISOString().slice(0, 16)
    });
  };

  const handleClear = () => {
    onFilterChange({});
  };

  return (
    <div className="bg-white shadow px-4 py-5 sm:rounded-lg sm:p-6 mb-6">
      <div className="md:grid md:grid-cols-4 md:gap-6">
        <div className="md:col-span-1">
          <h3 className="text-lg font-medium leading-6 text-gray-900">Filter Events</h3>
          <p className="mt-1 text-sm text-gray-500">
            Use these filters to narrow down the list of security events.
          </p>
        </div>
        <div className="mt-5 md:mt-0 md:col-span-3">
          <div className="grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-6">
            {/* Severity Filter */}
            <div className="sm:col-span-2">
              <label htmlFor="severity" className="block text-sm font-medium text-gray-700">
                Severity Level
              </label>
              <div className="mt-1">
                <select
                  id="severity"
                  name="severity"
                  value={filters.severity || ''}
                  onChange={handleChange}
                  className="shadow-sm focus:ring-blue-500 focus:border-blue-500 block w-full sm:text-sm border-gray-300 rounded-md"
                >
                  <option value="">All Severities</option>
                  <option value="Critical">Critical</option>
                  <option value="High">High</option>
                  <option value="Warning">Warning</option>
                  <option value="Medium">Medium</option>
                  <option value="Low">Low</option>
                </select>
              </div>
            </div>

            {/* Edge Agent Filter */}
            <div className="sm:col-span-2">
              <label htmlFor="celerway_edge_node_id" className="block text-sm font-medium text-gray-700">
                Edge Agent
              </label>
              <div className="mt-1">
                <select
                  id="celerway_edge_node_id"
                  name="celerway_edge_node_id"
                  value={filters.celerway_edge_node_id || filters.edge_node_id || ''}
                  onChange={handleChange}
                  disabled={edgeNodesLoading}
                  className="shadow-sm focus:ring-blue-500 focus:border-blue-500 block w-full sm:text-sm border-gray-300 rounded-md disabled:bg-gray-50 disabled:cursor-not-allowed"
                >
                  <option value="">All Edge Agents</option>
                  {edgeNodesLoading ? (
                    <option disabled>Loading...</option>
                  ) : edgeNodes && edgeNodes.length > 0 ? (
                    edgeNodes.map((node) => {
                      // Try multiple possible field names for the edge node ID
                      const nodeId = node.celerway_edge_node_id || 
                                     node.edge_node_id || 
                                     node.node_id || 
                                     node.id ||
                                     JSON.stringify(node); // Last resort: show the whole object
                      return (
                        <option key={nodeId} value={nodeId}>
                          {node.edge_node_id ? edgeNodeDisplayName(node) : nodeId}
                        </option>
                      );
                    })
                  ) : (
                    <option disabled>No agents found</option>
                  )}
                </select>
              </div>
              {edgeNodesError && (
                <p className="mt-2 text-sm text-red-600">{edgeNodesError}</p>
              )}
              {!edgeNodesLoading && edgeNodes.length > 0 && (
                <p className="mt-1 text-xs text-gray-500">
                  {edgeNodes.length} edge agent{edgeNodes.length !== 1 ? 's' : ''} available
                </p>
              )}
            </div>

            {/* Time Range Quick Filter */}
            <div className="sm:col-span-2">
              <label htmlFor="timeRange" className="block text-sm font-medium text-gray-700">
                Time Range
              </label>
              <div className="mt-1">
                <select
                  id="timeRange"
                  name="timeRange"
                  onChange={(e) => handleTimeRangeChange(e.target.value === '' ? null : parseInt(e.target.value))}
                  className="shadow-sm focus:ring-blue-500 focus:border-blue-500 block w-full sm:text-sm border-gray-300 rounded-md"
                >
                  <option value="">All Time</option>
                  <option value="1">Last Hour</option>
                  <option value="24">Last 24 Hours</option>
                  <option value="168">Last 7 Days</option>
                </select>
              </div>
            </div>

            {/* Start Time */}
            <div className="sm:col-span-3">
              <label htmlFor="start_time" className="block text-sm font-medium text-gray-700">
                Start Time
              </label>
              <div className="mt-1">
                <input
                  type="datetime-local"
                  name="start_time"
                  id="start_time"
                  value={filters.start_time || ''}
                  onChange={handleChange}
                  className="shadow-sm focus:ring-blue-500 focus:border-blue-500 block w-full sm:text-sm border-gray-300 rounded-md"
                />
              </div>
            </div>

            {/* End Time */}
            <div className="sm:col-span-3">
              <label htmlFor="end_time" className="block text-sm font-medium text-gray-700">
                End Time
              </label>
              <div className="mt-1">
                <input
                  type="datetime-local"
                  name="end_time"
                  id="end_time"
                  value={filters.end_time || ''}
                  onChange={handleChange}
                  className="shadow-sm focus:ring-blue-500 focus:border-blue-500 block w-full sm:text-sm border-gray-300 rounded-md"
                />
              </div>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex justify-end mt-6 space-x-3">
            <button
              type="button"
              onClick={handleClear}
              className="py-2 px-4 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            >
              Clear Filters
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EventFilters;
